import { Component, computed, Input, OnInit, viewChild } from '@angular/core';
import { PopupService } from '../../services/popup.service';
import { SchoolsPoiProperties } from '../../../menu/right-menu/layers-menu/education/education.service';
import { CrimeRateByYear } from '../../../menu/right-menu/layers-menu/points.service';
import { Feature } from 'geojson';
import {
  ARBITRARY_CELL,
  LayerStoreService,
} from '../../services/layer-store.service';
import { SelectedCellService } from '../../services/selected-cell.service';
import { UserAccessService } from '../../../../user/access/user-access.service';
import {
  availableInBulkFeatures,
  featuresToCompareToNationalPopup,
  hwwFeatures,
  LAND_AREA_FRACTION,
} from '../../../../shared/types/feature-data-type';
import { MapHttpService } from '../../services/map-http.service';
import { MapUrlService } from '../../services/map-url.service';
import { AsyncPipe, NgIf, NgSwitch, NgSwitchCase } from '@angular/common';
import { GroupedPopupListComponent } from './grouped-popup-list/grouped-popup-list.component';
import { SchoolsPopupListComponent } from './schools-popup-list/schools-popup-list.component';
import { OriPopupListComponent } from './ori-popup-list/ori-popup-list.component';
import { MatTooltip } from '@angular/material/tooltip';
import { IconComponent } from '../../../../shared/components/icon/icon.component';
import { IconButtonDirective } from '../../../../shared/directives/icon-button.directive';
import { ModalService } from '../../../../shared/services/modal.service';
import { MapModalData } from '../../../../user/map-redirect-modal/map-redirect-modal.component';
import type { MapboxGeoJSONFeature } from 'mapbox-gl';
import { Router } from '@angular/router';
import { catchError, of } from 'rxjs';
import { salesForecastPath } from '../../../../sales-forecast/sales-forecast.routes-data';
import { ColorscaleService } from '../colorscale/colorscale.service';
import { GoogleAnalyticsService } from '../../../../user/google-analytics.service';
import { openLockedFeaturePrompt } from '../../../menu/right-menu/layers-menu/shared/feature-tooltip/inacessible-features-util';
import { HWW_REQUEST_FEATURES, spreadHwwScopes } from './hww-grade';
import { POPUP_TAB_FEATURES } from './popup-groups';
import { USER_CATEGORY_KEY } from '../../../user-guide/user-guide.service';
import { TradeAreaActionComponent } from './trade-area-action/trade-area-action.component';
import { LEVEL_LABELS } from '../../services/level-labels';
import {
  TradeAreaService,
  tradeAreaLabel,
} from '../../services/trade-area.service';

/**
 * Tab metrics nothing else in this request chain fetches (e.g. HOUSEHOLDS).
 * Without them they only arrived via `feature.state` once the map was painted
 * with that layer, so unlocked rows vanished as empty. Tile-only names and HWW
 * indices (fetched separately) are filtered out.
 */
const POPUP_ONLY_FEATURES = POPUP_TAB_FEATURES.filter(
  (feature) =>
    !featuresToCompareToNationalPopup.includes(feature) &&
    !hwwFeatures.includes(feature) &&
    availableInBulkFeatures.includes(feature)
);

const DATA_REQUEST_FEATURES = [
  ...featuresToCompareToNationalPopup,
  ...POPUP_ONLY_FEATURES,
  LAND_AREA_FRACTION,
];

export const DEFAULT_POPUP = 'DEFAULT_POPUP';
export const SCHOOLS_POPUP = 'SCHOOLS_POPUP';
export const ORI_POPUP = 'ORI_POPUP';

export type PopupData = {
  type: typeof DEFAULT_POPUP | typeof SCHOOLS_POPUP | typeof ORI_POPUP;
  properties: Feature['properties'] | SchoolsPoiProperties | CrimeRateByYear;
  feature: MapboxGeoJSONFeature;
};

@Component({
  selector: 'app-map-popup',
  templateUrl: './map-popup.component.html',
  imports: [
    NgIf,
    AsyncPipe,
    IconComponent,
    NgSwitch,
    GroupedPopupListComponent,
    NgSwitchCase,
    SchoolsPopupListComponent,
    OriPopupListComponent,
    MatTooltip,
    IconButtonDirective,
    TradeAreaActionComponent,
  ],
  styleUrls: ['./map-popup.component.scss', '../../../../app.component.scss'],
})
export class MapPopupComponent implements OnInit {
  @Input() data!: PopupData;

  public title!: string;

  /**
   * The four HWW indices are a request of their own (see `loadAreaScore`), so
   * the score block can say it is working instead of showing a dash that reads
   * as "no data for this place".
   */
  public isScoreLoading = true;

  private isDataLoading = true;

  /**
   * Upper-cased metrics whose request is still out. The list draws them as
   * placeholders instead of dropping them as empty, so tabs and rows do not
   * appear one by one as the answers land.
   */
  public pendingFeatures: ReadonlySet<string> = this.collectPendingFeatures();

  private readonly tradeAreaRow = viewChild(TradeAreaActionComponent);

  /**
   * While this card's trade area is being analyzed. A phone shrinks the card
   * to that row (see the scss), so the area is visible while it loads. Every
   * outcome clears `pending`, which opens the card back up: success opens the
   * area's own card, and a failure leaves this one full size again.
   */
  public readonly isAnalyzingTradeArea = computed(
    () => !!this.tradeAreaRow()?.isAnalyzing()
  );

  constructor(
    private selectedCellService: SelectedCellService,
    public accessService: UserAccessService,
    private http: MapHttpService,
    public popupService: PopupService,
    public layerStore: LayerStoreService,
    private urlService: MapUrlService,
    private modalService: ModalService,
    private router: Router,
    private gaService: GoogleAnalyticsService,
    public colorScaleService: ColorscaleService,
    private tradeArea: TradeAreaService
  ) {}

  /**
   * "1-mile cell", "County", "Trade area · 10-min drive": what the title names,
   * read off the layer the card was opened from rather than the active level,
   * which stays a cell level while a trade area's card is open.
   */
  public get areaKind(): string {
    const level = this.data.feature.layer.id;

    if (level === ARBITRARY_CELL) {
      const analyzed = this.tradeArea.analyzed();
      return analyzed
        ? `Trade area · ${tradeAreaLabel(analyzed.shape)}`
        : 'Trade area';
    }

    const kind = LEVEL_LABELS[level]?.kind ?? '';
    return kind.charAt(0).toUpperCase() + kind.slice(1);
  }

  ngOnInit(): void {
    this.popupService.handlePopupTitle();

    const geoId = this.selectedCellService.getSelectedCellGeoId()!;
    this.loadWrittenInStateFeatures(geoId);
    this.loadAreaScore(geoId);
  }

  public handleClose(): void {
    this.popupService.handleClose();
  }

  public toggleSeeMore(): void {
    this.popupService.toggleSeeMore();
  }

  public handleDownloadXlsx(): void {
    this.popupService.downloadXlsx();
  }

  /**
   * Primary CTA. Everything the popup shows behind a padlock, plus the levels
   * and layers the plan gates, is what this sells - so it opens the same
   * subscription chooser the map's own paywall does rather than a
   * feature-specific upsell.
   */
  public handleGetFullAccess(): void {
    this.modalService.openSubscriptionsModal('all_packages');
  }

  /**
   * Orders a report on this exact tile. The service already knows the
   * selected cell, so the flow opens on its review step with the location
   * and report type filled in.
   */
  public handleOrderReport(): void {
    this.popupService.handleRequestReport();
  }

  // Reuses the same title already shown at the top of this popup (a
  // reverse-geocoded place name, e.g. "33139, Miami Beach, FL") as the
  // address the sales-forecast page's search bar runs on load — see
  // AddressInputComponent.prefillAndSearch(). Falls back to the feature's
  // raw name if the async reverse-geocode hasn't resolved yet.
  public goToSalesForecast(): void {
    const address =
      this.popupService.popupTitle ||
      (this.data.feature.properties?.['name'] as string | undefined);

    this.router.navigate([`/${salesForecastPath('my-business')}`], {
      queryParams: address ? { address } : {},
    });
  }

  /**
   * A locked metric was clicked in the grouped list. Routed through the same
   * modal and the same GA events as the padlock in the right-hand layers menu,
   * so the two upsell surfaces stay comparable in reporting.
   */
  public handleUnlockRequest(request: {
    feature?: string;
    label: string;
  }): void {
    if (this.accessService.getIsUnauthorized()) {
      this.gaService.logLockedParamClickGuest();
    } else {
      this.gaService.logLockedParamClickRegistered();
    }

    openLockedFeaturePrompt(
      request.feature,
      request.label,
      this.accessService,
      this.modalService
    );
  }

  /**
   * A readable metric in the popup was clicked: paint the map with it.
   *
   * The same single call the right menu's radio makes - LayersDataService is
   * subscribed to `activeLayer` and does the repaint, and the menu's own radio
   * is bound to the store, so it follows along. The popup stays open: its
   * pinned row is bound to `activeLayer` too, so the row that was clicked
   * becomes the one at the top of the card.
   */
  public handleColorByRequest(request: { feature: string }): void {
    this.layerStore.handleLayerChange(request.feature);
  }

  public handleShare(): void {
    this.urlService.copyUrlToClipboard();
  }

  public handleTooltipModalOpen(modalData: MapModalData): void {
    this.modalService.openModal(modalData);
  }

  private loadWrittenInStateFeatures(id: string) {
    this.http
      .getDataForSingleCell(
        id,
        this.selectedCellService.getCellType()!,
        DATA_REQUEST_FEATURES
      )
      // Caught so a failure ends the placeholders instead of leaving them on.
      .pipe(catchError(() => of(null)))
      .subscribe((data) => {
        this.isDataLoading = false;
        this.pendingFeatures = this.collectPendingFeatures();

        if (!data?.object) return;

        this.data.properties = { ...this.data.properties, ...data.object };
      });
  }

  /**
   * Fetches the four HWW indices for the score block at the top of the popup.
   *
   * They are not part of the request above because they come back nested by
   * scope and have to be flattened (see `spreadHwwScopes`). They are a
   * *separate* request for a blunter reason: this is the only feature name in
   * the popup that no curated list on the client vouches for, so a rejection
   * must not take the other forty metrics down with it. On failure the score
   * block falls back to whatever `feature.state` holds, which is exactly the
   * old behaviour.
   */
  private loadAreaScore(id: string) {
    this.http
      .getDataForSingleCell(
        id,
        this.selectedCellService.getCellType()!,
        HWW_REQUEST_FEATURES
      )
      .pipe(catchError(() => of(null)))
      .subscribe((data) => {
        this.isScoreLoading = false;
        this.pendingFeatures = this.collectPendingFeatures();

        if (!data?.object) return;

        this.data.properties = {
          ...this.data.properties,
          ...spreadHwwScopes(data.object),
        };
      });
  }

  /** A fresh Set per change, so the list's `ngOnChanges` sees it. */
  private collectPendingFeatures(): ReadonlySet<string> {
    return new Set(
      [
        ...(this.isDataLoading ? DATA_REQUEST_FEATURES : []),
        ...(this.isScoreLoading ? hwwFeatures : []),
      ].map((feature) => feature.toUpperCase())
    );
  }

  protected readonly ORI_POPUP = ORI_POPUP;
  protected readonly SCHOOLS_POPUP = SCHOOLS_POPUP;
  protected readonly DEFAULT_POPUP = DEFAULT_POPUP;
}
